import React from 'react';
import DeviceDropdown from '../dashboard/DeviceDropdown';
import IntervalButtons from '../dashboard/IntervalButtons';
import LoadingMessage from '../common/LoadingMessage';
import ErrorMessage from '../common/ErrorMessage';
import StatusTable from './StatusTable';
import { useStatusData } from '../../hooks/useStatusData';

function StatusDashboard({
    selectedDevice,
    onDeviceChange,
    selectedInterval,
    customRange,
    onIntervalChange,
    onRangeChange
}) {
    const { statusEvents, meta, loading, error, lastUpdated, refresh } = useStatusData(
        selectedDevice,
        selectedInterval,
        customRange
    );

    const hasSelection = Boolean(selectedDevice);
    const hasData = Array.isArray(statusEvents) && statusEvents.length > 0;

    return (
        <div className="dashboard-content">
            <header style={{ marginBottom: '20px' }}>
                <h2>Status Events</h2>
                <p className="section-intro">
                    Review periodic status reports from the device, including firmware version, temperature, and light levels.
                </p>
            </header>

            <section className="data-controls">
                <div className="data-controls__layout">
                    <div className="data-controls__device">
                        <div className="data-controls__title">Data analysis controls</div>
                        <DeviceDropdown selectedDevice={selectedDevice} onDeviceChange={onDeviceChange} />
                    </div>
                    <span className="data-controls__divider" aria-hidden="true" />
                    <div className="data-controls__interval">
                        <div className="data-controls__interval-label">Data interval</div>
                        <IntervalButtons
                            selectedInterval={selectedInterval}
                            onIntervalChange={onIntervalChange}
                            customRange={customRange}
                            onCustomRangeChange={onRangeChange}
                        />
                    </div>
                </div>
            </section>

            <div style={{ marginTop: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                    type="button"
                    onClick={refresh}
                    disabled={!hasSelection || loading}
                    className="refresh-button"
                >
                    🔄 Refresh
                </button>
                {lastUpdated && <span className="refresh-meta">Last updated: {lastUpdated.toLocaleTimeString()}</span>}
                {!hasSelection && !loading && !error && (
                    <span className="refresh-meta">Select a device to load status data.</span>
                )}
                {meta?.count !== undefined && (
                    <span className="refresh-meta">Total events: {meta.count}</span>
                )}
            </div>

            {error && (
                <div style={{ marginTop: '20px' }}>
                    <ErrorMessage message={error} title="Failed to load status events" />
                </div>
            )}

            {loading && (
                <div style={{ marginTop: '30px' }}>
                    <LoadingMessage message={hasSelection ? 'Loading status data…' : 'Select a device to load status data.'} />
                </div>
            )}

            {!loading && !error && hasSelection && (
                <div style={{ marginTop: '24px' }}>
                    {hasData ? (
                        <StatusTable items={statusEvents} />
                    ) : (
                        <p>No status events found for the selected period.</p>
                    )}
                </div>
            )}
        </div>
    );
}

export default StatusDashboard;
